import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarDays, MapPin, ArrowUpRight, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const DAYS   = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

/** Round DOWN to midnight in local time. */
const startOfDay = (d) => { const x = new Date(d); x.setHours(0,0,0,0); return x; };

/** Determine "this weekend" window (upcoming Sat + Sun in local time). */
const nextWeekend = () => {
  const now = new Date();
  const start = startOfDay(now);
  const dow = start.getDay();                     // 0 = Sunday, 6 = Saturday
  const daysUntilSat = (6 - dow + 7) % 7;         // 0 if today is Sat
  const sat = new Date(start); sat.setDate(sat.getDate() + daysUntilSat);
  const mondayAfter = new Date(sat); mondayAfter.setDate(sat.getDate() + 2);
  return { from: dow === 0 ? start : sat, to: mondayAfter };
};

const timeLabel = (iso) => {
  const d = new Date(iso);
  if (isNaN(d)) return '';
  const hh = d.getHours();
  const mm = d.getMinutes();
  if (hh === 0 && mm === 0) return 'All day';
  const h12 = ((hh + 11) % 12) + 1;
  const ampm = hh >= 12 ? 'pm' : 'am';
  return mm === 0 ? `${h12}${ampm}` : `${h12}.${String(mm).padStart(2,'0')}${ampm}`;
};

/**
 * Magazine tile — surfaces the next 3–5 events happening this Sat/Sun.
 * Falls back to "the next 5 upcoming events" if the weekend is empty so the
 * tile is never blank when there's still content worth showing.
 */
const WhatsOnThisWeekend = () => {
  const [events, setEvents] = useState([]);
  const [mode, setMode]     = useState('weekend'); // 'weekend' | 'upcoming'

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${API}/events?limit=50`);
        const raw = await r.json();
        const items = (Array.isArray(raw) ? raw : (raw.items || []))
          .filter((e) => e.published !== false && e.start_date);

        const now = new Date();
        const { from, to } = nextWeekend();
        const inRange = (e) => {
          const d = new Date(e.start_date);
          return d >= from && d < to;
        };
        const isFuture = (e) => new Date(e.start_date) >= startOfDay(now);
        const bySoonest = (a, b) => new Date(a.start_date) - new Date(b.start_date);

        const weekend = items.filter(inRange).sort(bySoonest).slice(0, 4);
        let list = weekend;
        // If the weekend is entirely empty, switch the tile to a general
        // "Upcoming events" showcase so we never render a blank section.
        if (list.length === 0) {
          list = items.filter(isFuture).sort(bySoonest).slice(0, 4);
          if (alive) setMode('upcoming');
        } else if (list.length < 4) {
          // Weekend has 1-3 events — backfill the rail with the next
          // upcoming events (dedup by id/slug) so the tile stays balanced.
          const usedKeys = new Set(list.map((e) => e.id || e.slug));
          const backfill = items
            .filter((e) => isFuture(e) && !usedKeys.has(e.id || e.slug))
            .sort(bySoonest)
            .slice(0, 4 - list.length);
          list = [...list, ...backfill];
        }
        if (alive) setEvents(list);
      } catch { /* empty */ }
    })();
    return () => { alive = false; };
  }, []);

  if (events.length === 0) return null;

  const [feature, ...rest] = events;
  const heroDate = feature.start_date ? new Date(feature.start_date) : null;

  return (
    <section className="bg-gradient-to-br from-rose-50 via-white to-amber-50" data-testid="home-whats-on-weekend">
      <div className="max-w-6xl mx-auto px-6 py-14">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-2">
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-rose-600 mb-1 inline-flex items-center gap-1.5">
              <CalendarDays size={11}/> {mode === 'weekend' ? "What's on this weekend" : 'Upcoming events'}
            </p>
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900" data-testid="home-weekend-h2">
              Plan your {mode === 'weekend' ? 'weekend' : 'week ahead'}
            </h2>
          </div>
          <Link
            to="/events"
            className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600 hover:text-rose-700 transition-colors"
            data-testid="home-weekend-cta"
          >
            View all events <ArrowUpRight size={13}/>
          </Link>
        </div>

        <div className="grid md:grid-cols-5 gap-4">
          {/* Feature (3/5) */}
          <Link
            to={`/events/${feature.slug}`}
            className="group relative md:col-span-3 aspect-[16/10] rounded-2xl overflow-hidden ring-1 ring-slate-200 bg-slate-200 block"
          >
            {feature.image_url ? (
              <img src={feature.image_url} alt={feature.title} loading="lazy"
                   className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                   onError={(e) => { e.target.style.opacity = '0.2'; }}/>
            ) : (
              <div className="absolute inset-0 bg-gradient-to-br from-rose-400 via-pink-500 to-amber-500"/>
            )}
            {heroDate && (
              <div className="absolute top-4 left-4 bg-white rounded-lg shadow-lg px-3 py-2 text-center min-w-[52px]">
                <p className="text-[9px] uppercase tracking-wider font-bold text-rose-600">{DAYS[heroDate.getDay()]}</p>
                <p className="text-2xl font-bold text-slate-900 leading-none mt-0.5">{heroDate.getDate()}</p>
                <p className="text-[9px] uppercase tracking-wider font-bold text-slate-500 mt-0.5">{MONTHS[heroDate.getMonth()]}</p>
              </div>
            )}
            <div className="absolute inset-x-0 bottom-0 p-5 bg-gradient-to-t from-black/90 via-black/60 to-transparent">
              <h3 className="text-white text-xl md:text-2xl font-bold leading-snug line-clamp-2">{feature.title}</h3>
              <div className="flex items-center gap-4 mt-2 text-white/85 text-xs flex-wrap">
                {heroDate && <span className="inline-flex items-center gap-1"><Clock size={11}/> {timeLabel(feature.start_date)}</span>}
                {feature.venue && <span className="inline-flex items-center gap-1"><MapPin size={11}/> {feature.venue}</span>}
              </div>
            </div>
          </Link>

          {/* Rail (2/5) */}
          <div className="md:col-span-2 flex flex-col gap-3">
            {rest.map((e) => {
              const d = e.start_date ? new Date(e.start_date) : null;
              return (
                <Link
                  key={e.id || e.slug}
                  to={`/events/${e.slug}`}
                  className="group flex items-center gap-3 bg-white ring-1 ring-slate-200 hover:ring-rose-300 hover:shadow-md rounded-xl p-3 transition-all"
                  data-testid={`weekend-event-${e.slug}`}
                >
                  {d && (
                    <div className="shrink-0 w-14 text-center bg-rose-50 rounded-lg py-2">
                      <p className="text-[9px] uppercase tracking-wider font-bold text-rose-600">{DAYS[d.getDay()]}</p>
                      <p className="text-xl font-bold text-slate-900 leading-none mt-0.5">{d.getDate()}</p>
                      <p className="text-[9px] uppercase tracking-wider font-bold text-slate-500 mt-0.5">{MONTHS[d.getMonth()]}</p>
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <h4 className="text-sm font-bold text-slate-900 leading-snug line-clamp-2 group-hover:text-rose-700">{e.title}</h4>
                    <div className="flex items-center gap-3 mt-1 text-xs text-slate-500 flex-wrap">
                      {d && <span className="inline-flex items-center gap-1"><Clock size={10}/> {timeLabel(e.start_date)}</span>}
                      {e.venue && <span className="inline-flex items-center gap-1 truncate"><MapPin size={10}/> <span className="truncate">{e.venue}</span></span>}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhatsOnThisWeekend;
